// libs/ui/src/primitives/TextArea.tsx
import { forwardRef, useCallback, type ComponentProps } from 'react';
import type { TextInput } from 'react-native';
import { styled, TextArea as TamaguiTextArea } from 'tamagui';
import type { RNTextInputCompat, RNTextInputCompatKeys } from './rnTextInputCompat';
import { useRowFocusFieldContext } from '../hooks/useRowFocus';
import {
  DEFAULT_INPUT_VARIANT,
  INLINE_INPUT_VARIANT,
  ERROR_INPUT_VARIANT_TRUE,
  DISABLED_INPUT_VARIANT_TRUE,
} from './inputVariants';

// TextArea shares Input's variant config — the focus-indicator rules
// are identical and the shape is pure data, so we reuse the same
// constants. See `inputVariants.ts` for the rationale.
const StyledTextArea = styled(TamaguiTextArea, {
  name: 'TextArea',
  color: '$textOnSurface',
  placeholderTextColor: '$textSubdued',
  outlineWidth: 0,
  outlineStyle: 'none' as never,

  variants: {
    variant: {
      default: DEFAULT_INPUT_VARIANT,
      inline: INLINE_INPUT_VARIANT,
    },
    error: {
      true: ERROR_INPUT_VARIANT_TRUE,
    },
    disabled: {
      true: DISABLED_INPUT_VARIANT_TRUE,
    },
  } as const,
});

type StyledTextAreaProps = ComponentProps<typeof StyledTextArea>;

export interface TextAreaStateProps {
  /** Paints `$borderError`, sets `aria-invalid`. */
  error?: boolean;
  /** Greys out, blocks editing, announces `aria-disabled`. */
  disabled?: boolean;
  /** `aria-describedby` target id — Phase 3 `FormRow` populates this. */
  describedById?: string;
}

export type TextAreaProps = Omit<StyledTextAreaProps, RNTextInputCompatKeys> &
  TextAreaStateProps &
  RNTextInputCompat;

/**
 * Multiline counterpart to `Input`. Reports focus to the surrounding
 * `RowFocusContext` so a `FormRow` / `FieldRow` / `FormRow` containing
 * notes will highlight on focus consistently with single-line fields.
 * Picks up `error` / `disabled` / `aria-describedby` from a `FormRow`
 * parent automatically (ADR-0075).
 */
type TextAreaFocusHandler = NonNullable<StyledTextAreaProps['onFocus']>;
type TextAreaBlurHandler = NonNullable<StyledTextAreaProps['onBlur']>;
type TextAreaFocusEvent = Parameters<TextAreaFocusHandler>[0];
type TextAreaBlurEvent = Parameters<TextAreaBlurHandler>[0];

// eslint-disable-next-line @typescript-eslint/no-shadow -- the inner function expression is deliberately named after the const so React DevTools and stack traces show the component name; it is not a real shadow.
export const TextArea = forwardRef<TextInput, TextAreaProps>(function TextArea(props, ref) {
  const fieldContext = useRowFocusFieldContext();
  const { onFocus, onBlur, variant, error, disabled, describedById, id, editable, ...rest } =
    props;

  const resolvedError = error ?? fieldContext.error;
  const resolvedDisabled = disabled ?? fieldContext.disabled;
  const resolvedDescribedBy = describedById ?? fieldContext.messageId;
  const resolvedId = id ?? fieldContext.controlId;

  const reportRowFocus = fieldContext.reporter;
  const resolvedVariant = variant ?? (reportRowFocus ? 'inline' : 'default');

  const handleFocus = useCallback(
    (e: TextAreaFocusEvent) => {
      reportRowFocus?.(true);
      onFocus?.(e);
    },
    [reportRowFocus, onFocus],
  );

  const handleBlur = useCallback(
    (e: TextAreaBlurEvent) => {
      reportRowFocus?.(false);
      onBlur?.(e);
    },
    [reportRowFocus, onBlur],
  );

  // Single cast — Tamagui's typed props don't compose cleanly with
  // exactOptionalPropertyTypes when spread through forwardRef.
  const allProps: Record<string, unknown> = {
    ...rest,
    id: resolvedId,
    variant: resolvedVariant,
    error: resolvedError,
    disabled: resolvedDisabled,
    // v2 derives editability from `disabled` / `readOnly`; map an explicit
    // `editable={false}` onto `readOnly` (see rnTextInputCompat).
    ...(editable === false ? { readOnly: true } : null),
    ...(resolvedError ? { 'aria-invalid': true } : null),
    ...(resolvedDisabled ? { 'aria-disabled': true } : null),
    ...(resolvedDescribedBy ? { 'aria-describedby': resolvedDescribedBy } : null),
    accessibilityState: { disabled: !!resolvedDisabled },
    onFocus: handleFocus,
    onBlur: handleBlur,
    ref,
  };
  return <StyledTextArea {...allProps} />;
});
